import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';
import { useResumeSearchAlerts } from '@hooks/filters/useResumeSearchAlerts';
import {
  Button, Dropdown, DropdownButton, DropdownItems, DropdownItem,
} from '@components/common';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { ClockRotateLeftIcon } from '@components/common/Icons';
import { filterStyles } from '../helpers/filterStyles';


// Interface
// ******************************************
interface ResumeSearchAlertsProps {
  locale?: string,
  setQueryParams: Dispatch<SetStateAction<object>>,
  sortQueryParams?: boolean,
}


/**
 * @description Renders the resume search alerts dropdown.
 * - **locale** The locale of the app
 * - **setQueryParams** The set query params function (get it from parent)
 * - **sortQueryParams** If true, the query params will be sorted alphabetically. Default true. Needed to maintain the
 * order of the query params for SWR access keys.
 */
export const ResumeSearchAlerts = (props: ResumeSearchAlertsProps) => {
  // Destructure props
  const {
    locale = 'ro',
    setQueryParams,
    sortQueryParams = true,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // Resume search alerts hook
  // ******************************
  const {
    searchAlerts,
    deleteAlert,
    setSelectedFilters,
  } = useResumeSearchAlerts({ locale, sortQueryParams });


  // Return null if no alerts
  // ******************************
  if (!searchAlerts || searchAlerts.length === 0) return null;


  // Render component
  // ******************************
  return (
    <Dropdown className="relative leading-none lg:pl-3">
      {/* Dropdown trigger button */}
      <DropdownButton as={Button} styling="text" size="sm" color="ink" className="!text-left !font-medium outline-0">
        <ClockRotateLeftIcon className="mr-1.5 size-4.5 shrink-0" />
        {`${t('user.saved.searches')}: ${searchAlerts && searchAlerts?.length > 0 ? searchAlerts?.length : 0}`}
        <ChevronDownIcon className="ml-1 size-4 shrink-0" />
      </DropdownButton>

      {/* Dropdown panel */}
      <DropdownItems
        animation="slideDown"
        position="right"
        className={`${filterStyles.listboxOptionsWrapper} max-w-80 py-2`}
      >
        {
          searchAlerts?.map((alert) => (
            <DropdownItem
              key={alert.id}
              as="div"
              className={`${filterStyles.listboxOption} justify-between !py-1 !pl-3`}
            >
              {/* Label */}
              <Button
                styling="none"
                className="line-clamp-3 grow py-1 text-left"
                onClick={() => setSelectedFilters(alert?.resumeSearch, setQueryParams)}
              >
                <span className="line-clamp-3 text-ink">{alert?.label}</span>
              </Button>

              {/* Delete button */}
              <Button
                styling="none"
                className="group py-1 pl-2.5"
                onClick={() => { if (alert?.id) deleteAlert(alert.id); }}
              >
                <span className={filterStyles.closeButtonIcon}>
                  <XMarkIcon className="size-4" />
                </span>
              </Button>

            </DropdownItem>
          ))
        }
      </DropdownItems>
    </Dropdown>
  );
};
